import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

export const API = process.env.REACT_APP_BACKEND_URL;
const TZ = 'America/Los_Angeles';
export const fmt = (iso, o) => (iso ? new Date(iso).toLocaleString('en-US', { timeZone: TZ, ...o }) : '');
const isDay = (d) => /^\d{4}-\d{2}-\d{2}$/.test(d || '');
export const dayLabel = (d, o = { month: 'short', day: 'numeric', year: 'numeric' }) =>
  !d ? '—' : isDay(d) ? new Date(`${d}T12:00:00Z`).toLocaleDateString('en-US', { ...o, timeZone: 'UTC' }) : fmt(d, o);
export const longDate = (d) => dayLabel(d, { month: 'long', day: 'numeric', year: 'numeric' });
export const timeLabel = (iso) => fmt(iso, { hour: 'numeric', minute: '2-digit' });
export const todayPT = () => new Date().toLocaleDateString('en-CA', { timeZone: TZ });
export const money = (c) => `$${((c || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const dollars = (v) => (v == null ? null : money(Math.round(Number(v) * 100)));
export const newId = () => window.crypto?.randomUUID?.() || `r-${Date.now()}-${Math.random().toString(36).slice(2)}`;
export const propName = (a) => (a ? a.label || a.line1 || 'Your property' : 'Property');
export const propAddr = (a) => [a?.line1, a?.line2, a?.city, a?.state, a?.zip].filter(Boolean).join(', ');
export const initials = (n) => (n || '').split(' ').filter(Boolean).map((w) => w[0]).slice(0, 2).join('').toUpperCase() || 'GG';
export const errText = (e) => {
  const d = e?.response?.data?.detail;
  const t = typeof d === 'string' ? d : Array.isArray(d) ? d[0]?.msg : null;
  return t ? t.replace(/^Value error, /, '') : e?.response ? 'Something went wrong. Please try again.' : 'You appear to be offline. Your entry is kept — try again when connected.';
};

export const useHeaders = () => {
  const { token } = useAuth();
  return useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
};

export const useLoad = (path) => {
  const headers = useHeaders();
  const [s, setS] = useState({ data: null, error: null });
  const reload = useCallback(() => axios.get(`${API}${path}`, { headers })
    .then((r) => setS({ data: r.data, error: null }))
    .catch((e) => setS((x) => ({ data: x.data, error: errText(e) }))), [path, headers]);
  useEffect(() => { reload(); }, [reload]);
  return [s.data, reload, s.error];
};

// Submit once at a time; the request id is reused across retries so the server can de-duplicate.
export const useSubmit = () => {
  const [busy, setBusy] = useState(false);
  const rid = useRef(newId());
  const run = async (fn) => {
    if (busy) return undefined;
    setBusy(true);
    try { const out = await fn(rid.current); rid.current = newId(); return out; } finally { setBusy(false); }
  };
  return [busy, run];
};

const PortalCtx = createContext(null);
export const DrawerLabelCtx = createContext('YOUR CLIENT PORTAL');
export const usePortal = () => useContext(PortalCtx);

export const PortalProvider = ({ children }) => {
  const [toastMsg, setToast] = useState('');
  const [drawer, setDrawer] = useState(null);
  const timer = useRef();
  const toast = useCallback((m) => { setToast(m); clearTimeout(timer.current); timer.current = setTimeout(() => setToast(''), 4000); }, []);
  const value = useMemo(() => ({ toast, openDrawer: setDrawer, closeDrawer: () => setDrawer(null) }), [toast]);
  return (
    <PortalCtx.Provider value={value}>
      {children}
      <Drawer drawer={drawer} onClose={() => setDrawer(null)} toastNode={drawer ? <Toast msg={toastMsg} /> : null} />
      {!drawer && <Toast msg={toastMsg} />}
    </PortalCtx.Provider>
  );
};

const Toast = ({ msg }) => <div id="toast" role="status" aria-live="polite" className={msg ? 'show' : ''} data-testid="portal-toast">{msg}</div>;

const Drawer = ({ drawer, onClose, toastNode }) => {
  const ref = useRef(null);
  const label = useContext(DrawerLabelCtx);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (drawer && !d.open) d.showModal();
    if (!drawer && d.open) d.close();
  }, [drawer]);
  return (
    <dialog ref={ref} aria-labelledby="drawer-title" onClose={onClose} onCancel={onClose} data-testid="portal-drawer">
      <div className="drawer-head"><div><span className="eyebrow">{drawer?.label || label}</span><h2 id="drawer-title">{drawer?.title}</h2></div>
        <button onClick={onClose} aria-label="Close panel" data-testid="drawer-close">×</button></div>
      <div id="drawer-body">{drawer?.content}</div>
      {toastNode}
    </dialog>
  );
};

export const Welcome = ({ title, desc, action, eyebrow = 'YOUR SPACE. YOUR TEAM.' }) => (
  <section className="welcome"><div><span className="eyebrow">{eyebrow}</span><h1>{title}</h1><p>{desc}</p></div>{action}</section>
);
export const Badge = ({ children, testId }) => <span className="badge" data-testid={testId}>{children}</span>;
export const Row = ({ k, v, testId }) => <div className="row"><span>{k}</span><strong data-testid={testId}>{v}</strong></div>;
export const Loading = () => <p className="empty" data-testid="portal-loading">Loading…</p>;
export const LoadError = ({ text, onRetry }) => (
  <div className="panel empty" data-testid="portal-load-error">{text} <button className="text-button" onClick={onRetry}>Try again</button></div>
);
export const Field = ({ label, name, ...p }) => (
  <div className="field"><label htmlFor={`f-${name}`}>{label}</label><input id={`f-${name}`} name={name} data-testid={`field-${name}`} {...p} /></div>
);
export const TextField = ({ label, name, ...p }) => (
  <div className="field"><label htmlFor={`f-${name}`}>{label}</label><textarea id={`f-${name}`} name={name} data-testid={`field-${name}`} {...p} /></div>
);
export const SelectField = ({ label, name, options, ...p }) => (
  <div className="field"><label htmlFor={`f-${name}`}>{label}</label>
    <select id={`f-${name}`} name={name} data-testid={`field-${name}`} {...p}>{options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
);
export const formValues = (e) => Object.fromEntries(new FormData(e.currentTarget).entries());
